import { maybeQuoteLabelName, metricHasUTF8Characters, quoteString } from '@chronosphereio/editors/prometheus';
import { CamelCasedProperties } from 'type-fest';
import {
  AppMetadataProps,
  isEmptyObject,
  METRIC_NAME_LABEL_KEY,
  PrometheusQuerySpec,
  PrometheusTimeSeriesQueryDefinition,
  SeriesNameType,
} from '@/core';
import { renameSeriesWithLabels, renameSeriesWithRegex } from '@/plugin-system/utils/rename';
import { parseSampleValue } from '../model';
import { Metric } from '../model/api-types';

/**
 * Types for metric labels, used in series_name_format implementation
 */
export type SeriesLabels = Record<string, string>;

/**
 * Builds a PromQL label selector string (just the {labels} part).
 * Handles quoting for label names that contain special characters.
 *
 * @param labels - Object of label name/value pairs
 * @returns A valid PromQL label selector string
 *
 * Examples:
 * - buildLabelSelector({job: "node"}) => '{job="node"}'
 * - buildLabelSelector({"foo.bar": "baz"}) => '{"foo.bar"="baz"}'
 */
function buildLabelSelector(labels: { [key: string]: unknown }): string {
  const parts: string[] = [];

  Object.keys(labels)
    .sort()
    .forEach((labelName) => {
      const labelValue = labels[labelName];
      if (labelValue !== undefined) {
        parts.push(`${maybeQuoteLabelName(labelName)}="${labelValue}"`);
      }
    });

  return `{${parts.join(',')}}`;
}

/**
 * Builds a PromQL label selector string using JSON-style format.
 *
 * @param labels - Object of label name/value pairs
 * @returns A JSON-style label selector string
 *
 * Examples:
 * - buildJsonLabelSelector({job: "node"}) => '{"job":"node"}'
 */
function buildJsonLabelSelector(labels: { [key: string]: unknown }): string {
  const parts: string[] = [];

  Object.keys(labels)
    .sort()
    .forEach((labelName) => {
      const labelValue = labels[labelName];
      if (labelValue !== undefined) {
        parts.push(`"${labelName}":"${labelValue}"`);
      }
    });

  return `{${parts.join(',')}}`;
}

/**
 * Builds a full PromQL selector string with metric name and labels.
 * Handles quoting for both metric names and label names that contain special characters.
 *
 * @param metricName - The metric name (from __name__ label)
 * @param labels - Object of label name/value pairs
 * @returns A valid PromQL selector string
 *
 * Examples:
 * - buildPromQLSelector("my_metric", {job: "node"}) => 'my_metric{job="node"}'
 * - buildPromQLSelector("my.metric", {job: "node"}) => '{"my.metric",job="node"}'
 * - buildPromQLSelector("my.metric", {"foo.bar": "baz"}) => '{"my.metric","foo.bar"="baz"}'
 */
function buildPromQLSelector(metricName: string, labels: { [key: string]: unknown }): string {
  if (metricHasUTF8Characters(metricName)) {
    // Metric name needs quoting - put it inside braces as first element
    const labelParts: string[] = [];

    Object.keys(labels)
      .sort()
      .forEach((labelName) => {
        const labelValue = labels[labelName];
        if (labelValue !== undefined) {
          labelParts.push(`${maybeQuoteLabelName(labelName)}="${labelValue}"`);
        }
      });

    if (labelParts.length > 0) {
      return `{${quoteString(metricName)},${labelParts.join(',')}}`;
    }
    return `{${quoteString(metricName)}}`;
  }

  // Metric name doesn't need quoting - prepend it outside braces
  return `${metricName}${buildLabelSelector(labels)}`;
}

/*
 * Metric labels formatter which checks for __name__ and outputs valid PromQL for series name
 */
export function getUniqueKeyForPrometheusResult(
  metricLabels: {
    [key: string]: string;
  },
  { removeExprWrap }: { removeExprWrap?: boolean } = {}
) {
  if (!metricLabels) {
    return '';
  }

  const metricName = metricLabels[METRIC_NAME_LABEL_KEY];
  const otherLabels = metricName !== undefined ? { ...metricLabels, [METRIC_NAME_LABEL_KEY]: undefined } : metricLabels;

  if (removeExprWrap) {
    return buildJsonLabelSelector(otherLabels);
  }

  if (metricName !== undefined) {
    return buildPromQLSelector(metricName, otherLabels);
  }

  return buildLabelSelector(otherLabels);
}

export type GetFormattedPrometheusSeriesNameOpts = CamelCasedProperties<
  Pick<PrometheusQuerySpec, 'query' | 'series_name' | 'series_name_format'>
> & {
  metric: Metric;
  alias?: string;
};

/*
 * Determine human-readable series name to be used in legend and tooltip
 */
export function getFormattedPrometheusSeriesName(args: GetFormattedPrometheusSeriesNameOpts) {
  const { query, seriesNameFormat, seriesName, metric, alias } = args;

  // Name the series after the metric labels by default.
  // Use the query if no metric or metric labels are empty.
  let name = getUniqueKeyForPrometheusResult(metric);
  if (name === '' || isEmptyObject(metric)) {
    name = query;
  }

  // The query editor allows users to choose a way to rename each series.
  // These names will appear in the legend and the tooltip.
  let formattedName = name;

  if (seriesName) {
    switch (seriesName.type) {
      case SeriesNameType.Regex:
        formattedName = renameSeriesWithRegex(name, seriesName);
        break;
      case SeriesNameType.Labels:
        // Disabling to avoid refactoring when enabling eslint:recommended rules.
        // eslint-disable-next-line no-case-declarations
        const { name_pattern } = seriesName;
        formattedName = name_pattern ? renameSeriesWithLabels(name_pattern, metric) : name;
        break;
    }
  } else if (seriesNameFormat) {
    formattedName = renameSeriesWithLabels(seriesNameFormat, metric);
  }

  return { name, formattedName: withAlias(formattedName, query, alias) };
}

/*
 * withAlias generates the series name with an alias if provided.
 */
const withAlias = (name: string, query: string, alias: string | undefined) => {
  if (name === query) {
    return alias ? alias : query;
  }
  return alias ? `${alias} ${name}` : name;
};

/**
 * Convert AppMetadataProps to HTTP headers
 */
export function appMetadataToHTTPHeaders(metadata: AppMetadataProps) {
  return {
    'X-Chronosphere-App': metadata.app || 'unknown-viz-framework',
    'X-Chronosphere-App-Metadata-JSON': JSON.stringify(metadata.metadata || {}),
  };
}

/**
 * Returns the value of a Prometheus label
 */
export function getLabelValue(metric: Metric, label: string): string | number {
  // Disabling to avoid refactoring when enabling eslint:recommended rules.
  // eslint-disable-next-line no-prototype-builtins
  if (metric.hasOwnProperty(label)) {
    const metricValue = metric[label];
    if (label === 'le' && metricValue) {
      return parseSampleValue(metricValue);
    }
    return metricValue ?? '';
  }
  return '';
}

export function injectDownsamplingStrategy(
  query: string,
  downsampleStrategy: PrometheusTimeSeriesQueryDefinition['spec']['downsampling_strategy']
) {
  const SUB_QUERY_INTERVAL = '15s';
  if (!downsampleStrategy || downsampleStrategy === 'LTTB') {
    return query;
  }
  return `${downsampleStrategy.toLowerCase()}_over_time(${query}[$__interval:${SUB_QUERY_INTERVAL}])`;
}
